import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/apiUrl';

const AuthContext = createContext(null);

axios.interceptors.request.use((config) => {
  if (config._skipAuth) return config;
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { timeout: 10000 });
      setUser(data);
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, {
        email: email.trim().toLowerCase(),
        password: password.trim(),
      }, {
        _skipAuth: true,
        timeout: 15000,
        headers: { 'Content-Type': 'application/json' },
      });
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      const userData = {
        id: data.id, email: data.email, name: data.name,
        role: data.role, permissions: data.permissions,
        vendor_brand: data.vendor_brand,
      };
      setUser(userData);
      return { success: true, role: data.role };
    } catch (error) {
      if (error.response) {
        const rd = error.response.data;
        const detail = typeof rd === 'object' ? rd?.detail : rd;
        return { success: false, error: typeof detail === 'string' ? detail : 'E-posta veya şifre hatalı.' };
      }
      if (error.code === 'ECONNABORTED') {
        return { success: false, error: 'Sunucu yanıt vermedi. Lütfen tekrar deneyin.' };
      }
      console.error('Login network error:', error.message, error.code);
      return { success: false, error: 'Sunucuya bağlanılamadı. Lütfen sayfayı yenileyip tekrar deneyin.' };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API_URL}/api/auth/logout`); } catch {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(false);
  };

  const hasPerm = (perm) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return !!user.permissions?.[perm];
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, hasPerm }}>
      {children}
    </AuthContext.Provider>
  );
};
